import * as React from 'react';
import './_CommentsInput.scss';
import { Button, ButtonDropdown, Icon, Input } from 'kui';
import { ICommentInputProps } from './types';
import { CLASS_COMMENTS, } from '../../constants';
import { debounce, isEqual } from 'underscore';
import { getParentsClasses } from '../../../../../helpers/getParentsClasses';
import {
    CLASS_COMMENTS_INPUT,
    COMMENT_EDIT_LABEL,
    COMMENT_LABEL,
    COMMENT_NOT_TITLE_LABEL,
    COMMENT_PLACEHOLDER,
    COMMENT_REPLY_ANIMATION_DURATION,
    COMMENTS_INPUT_GOOGLE_THUMB_SIZE,
    DEFAULT_INPUT_HEIGHT
} from './constants';
import { GOOGLE_SPACING, THUMB_COMMENT_REPLY_SIZE } from 'app/const';
import { blockMarkdown } from 'app/helper/markdownHelper';
import { EEditorType } from 'app/view/react_components/base/components/HtmlEditor/types';
import { EHTMLEditorType } from 'app/types/types';
import { TDriveDocId } from 'app/types/rest/IRestDriveDoc';
import { CommentAttachments } from '../CommentAttachments/CommentAttachments';
import { msFileThumbnailStorage } from 'app/helper/authorisation/microsoft/storage/msThumbnail';
import { googleFileThumbnailStorage } from 'app/helper/authorisation/google/storage/googleThumbnail';
import { EAuthUserPlatformType } from 'app/types/rest/IRestAuthUser';
import { IDriveDoc } from 'app/store/model/card/types/IDriveDoc';
import { AnalyzeRender } from '../../../../../helpers/memoizeHelper';
import { ECommentAddMode } from '../../../../../../../types/rest/IUserMeta';
import {
    HtmlEditorHOC
} from '../../../../../aside_panel/cardDetails/DescriptionSection/hocs/HtmlEditorHOC/HtmlEditorHOC';
import { CommentsMentionsHOC } from '../../hocs/CommentsMentionsHOC/CommentsMentionsHOC';
import {
    CardAttachmentAdd
} from '../../../../../aside_panel/cardDetails/AttachmentsSection/components/CardAttacmentAdd/CardAttacmentAdd';
import {
    getBigPreviewIcon
} from '../../../../../aside_panel/cardDetails/AttachmentsSection/hocs/CardAttachmentsSectionHOC/helpers/getBigPreviewIcon';
import { CARD_SECTION_COMMENTS_ADD_ATTACHMENT } from '../../../../../aside_panel/cardDetails/CommentsSection/constants';
import { root } from '../../../../../../../store/constants';
import { CLASS_ASIDE_PANEL_BODY } from '../../../../../aside_panel/asidePanel/components/AsidePanelBody/constants';
import { CLASS_ASIDE_PANEL } from '../../../../../aside_panel';
import {
    CLASS_ASIDE_PANEL_STATIC,
    CLASS_ASIDE_PANEL_STATIC_NO_HEADER
} from '../../../../../aside_panel/asidePanel/constants';

// @ts-ignore
const analyzeRender = window.Settings.development ? new AnalyzeRender(`CommentsInput`) : null;

//to prevent function clear when rerender
const debouncedCommentHelper = debounce((
    callback: any
) => {
    callback();
}, 200);

export function CommentsInput({
    addMode,
    autoFocus,
    boardId,
    cardId,
    commentsCount,
    driveDocs,
    droppedAttachments,
    editComment: _editComment,
    HTMLEditorType,
    isMentionsOpened,
    isShow,
    isShowReplyAttachment,
    isShowSelectFile,
    platformType,
    openedProperty,
    replyAttachment,
    replyComment,
    replyText,
    value,
    onAddAttachments,
    onAttachmentsChange,
    onBlur,
    onCancel,
    onChangeCommentValue,
    onChangeMention,
    onClose,
    onCreate,
    onDidMount,
    onKeyDown,
    onRemoveAttachment,
    onReplyClose,
    onSaveChanges,
    onScrollToComment,
    onScrollTop,
    onSelectClick,
    onUploadClick,
    setInputHeight,
}: ICommentInputProps) {
    if (analyzeRender) analyzeRender.call(`CommentsInput ${cardId}`);

    const isVirtualList = !!setInputHeight;

    let [inputValue, _setInputValue] = React.useState(value);
    let [valueEditor, _setValueEditor] = React.useState(value);
    const [attachments, setAttachments] = React.useState(driveDocs);
    const [isChanged, setChanged] = React.useState(null);
    const [isSendDisabled, setSendDisabled] = React.useState(null);
    const [removeDriveDocIds, setRemoveDriveDocIds] = React.useState([]);
    const [isShowReply, setShowReply] = React.useState(null);
    const [isShowAttachments, setShowAttachments] = React.useState(null);
    const [editComment, setEditComment] = React.useState(null);
    const [isOpen, setOpen] = React.useState(null);
    const [isFloating, setFloating] = React.useState(null);

    const className = CLASS_COMMENTS_INPUT;
    const buttonClass = className + '__send';
    const classAttachment = className + '__add-attachment';
    const replyClass = className + '__reply';

    const ref = React.useRef(null);
    const inputRef = React.useRef(null);

    const editorOnEnter= isMentionsOpened
        ? {onEnter: () => {}} // если в HtmlEditor передать onEnter, то у quill отключаётся все привязки на Enter
        : {onCtrlEnter: handleHtmlCtrlEnter};

    function handleHtmlCtrlEnter() {
        if (isSendDisabled) return;

        onChangeCommentValue(valueEditor);
        onCreate({ctrlKey: true} as any, attachments).then(id => {
            setTimeout(() => onScrollToComment(id), 0);
        });
        closeHandle();
    }

    function handleHtmlChange(value: string) {
        if (valueEditor === value) return;

        setValueEditor(value);
        if (isMentionsOpened) {
            onChangeCommentValue(valueEditor);
        }
        setTimeout(() => {
            const w = window as any;
            if (w.mentionPasted) return;

            onChangeMention(null, null);
        }, 0);
    }

    const setInputValue = (value: string) => {
        inputValue = value;
        _setInputValue(value);
    };

    const setValueEditor = (value: string) => {
        valueEditor = value;
        _setValueEditor(value);
    };

    function changeHandler(e: any) {
        const {target, key} = e;
        const {value, selectionStart} = target;

        setInputValue(value);
        setValueEditor(value);

        if (!editComment) {
            const debouncedChange = () => {
                onChangeCommentValue(value || ''); // undefined on clear
                onChangeMention(key, selectionStart || 0); // undefined on clear
            };
            debouncedCommentHelper(debouncedChange);
        }
    }

    function enterHandle(event: React.KeyboardEvent<HTMLElement>) {
        if (
            addMode === ECommentAddMode.CTRL_ENTER && (event.ctrlKey || event.metaKey) ||
            addMode === ECommentAddMode.ENTER && !event.ctrlKey && !event.metaKey
        ) {
            createHandle(event);
        }
        if (addMode === ECommentAddMode.ENTER && (event.ctrlKey || event.metaKey)) {
            setInputValue(inputValue + '\n');
            setValueEditor(valueEditor + '\n');
        }
        if (addMode === ECommentAddMode.ENTER && !event.ctrlKey && !event.metaKey) {
            event.preventDefault(); // не добавлять строчку
        }
    }

    function createHandle(event: React.KeyboardEvent<HTMLElement>) {
        const text = isMarkdown ? inputValue : valueEditor;
        if (isSendDisabled || editComment && !isChanged) {
            closeHandle();
            return;
        }
        reset();
        if (editComment) {
            const promises = removeDriveDocIds.map(id => {
                return onRemoveAttachment(id);
            });
            Promise.all(promises).then(() => {
                onSaveChanges(text, attachments).then(id => {
                    setTimeout(() => onScrollToComment(id), 0);
                }).finally(() => {
                    closeHandle();
                });
            });
        } else {
            onCreate(event, attachments).then(id => {
                setTimeout(() => onScrollToComment(id), 0);
            }).finally(() => {
                closeHandle();
            });
        }
    }

    function closeHandle() {
        if (editComment) {
            onCancel();
        } else {
            if (inputRef && inputRef.current) {
                if (inputRef.current.getInput() && inputRef.current.getInput().closest(`.${CLASS_COMMENTS + '-list'}`)) {
                    inputRef.current.getInput().closest(`.${CLASS_COMMENTS + '-list'}`).focus();
                }
            }
            onClose();
        }
        reset();
    }

    function reset() {
        setAttachments([]);
        setRemoveDriveDocIds([]);
        setInputValue('');
        setValueEditor('');
        setShowAttachments(false);
        setShowReply(false);
        if (setInputHeight) setInputHeight(ref.current && ref.current.offsetHeight);
        setExtraHeight(0);
        setEditComment(null);
    }

    const fixInput = () => {
        setTimeout(() => { // подождать когда отрисуются аттачменты
            const input = inputRef.current && inputRef.current.getInput();
            if (input.getBoundingClientRect().bottom + GOOGLE_SPACING >= root._innerHeight) { // + scroll
                const asidePanel = document.querySelector('.' + CLASS_ASIDE_PANEL_BODY) as HTMLElement;
                asidePanel.scrollTop = asidePanel.scrollHeight;
            }
        }, 0);
    }

    function keyDownHandle(event: React.KeyboardEvent<HTMLInputElement>) {
        if (event.key === 'Escape' && !isMentionsOpened) {
            event.stopPropagation(); // to avoid the aside-panel closure on esc if input is in focus
            closeHandle();
        } else {
            onKeyDown(event);
        }
    }
    function onBlurHandle (event: React.FocusEvent) {
        const className = CLASS_COMMENTS + '-mentions__dropdown';
        const targetClasses = getParentsClasses(
            event.relatedTarget as HTMLElement,
            [className, classAttachment]
        );
        if (
            targetClasses.includes(className) ||
            targetClasses.includes(classAttachment)
        ) {
            event.stopPropagation();
            return;
        }

        onBlur();
    }

    const onDeleteAttachment = (id: TDriveDocId, fileId: string) => {
        const idx = driveDocs.findIndex(item => item.id === id);
        if (idx > -1) {
            setRemoveDriveDocIds([...removeDriveDocIds, id]);
        }
        const newAttachments = attachments.filter(item => item.fileId !== fileId);
        if (attachments.length === 1) {
            setShowAttachments(false);
            setTimeout(() => {
                setAttachments(newAttachments);
                onAttachmentsChange(newAttachments);
            }, COMMENT_REPLY_ANIMATION_DURATION);
        } else {
            setAttachments(newAttachments);
            onAttachmentsChange(newAttachments);
        }
        const input = inputRef.current && inputRef.current.getInput();
        if (input) input.focus();
    };

    const onCommentClick = () => {
        const id = editComment ? editComment.id : replyComment.id;
        onScrollToComment(id);
    };

    const afterAttachmentAdd = (newAttachments: IDriveDoc[]) => {
        setAttachments([...attachments, ...newAttachments]);
        if (!isVirtualList) fixInput();
    };

    const onAttachmentAdd = () => {
        onAddAttachments(attachments, (newAttachments) => {
            setAttachments([...attachments, ...newAttachments]);
            onAttachmentsChange([...attachments, ...newAttachments]);
            if (!isVirtualList) fixInput();
        });
        const input = inputRef.current && inputRef.current.getInput();
        if (!input) return;
        input.focus();
    };

    React.useEffect(() => {
        if (isVirtualList) return;
        const input = inputRef.current && inputRef.current.getInput();
        if (value && value.length && input === document.activeElement) {
            if (input.getBoundingClientRect().bottom + GOOGLE_SPACING >= root._innerHeight) { // + scroll
                const scrollEl = document.querySelector('.' + CLASS_ASIDE_PANEL_BODY) as HTMLElement;
                scrollEl.scrollTo({ top: scrollEl.scrollTop + scrollEl.scrollHeight, behavior: 'smooth' });
            }
        }
    }, [value]);

    React.useEffect(() => {
        if (!setInputHeight) return;

        const onResize = () => {
            setInputHeight(isShow ? ref.current && ref.current.offsetHeight || DEFAULT_INPUT_HEIGHT : 0);
        }
        onResize();

        root.addEventListener('resize', onResize);

        return () => root.removeEventListener('resize', onResize);
    }, [ref.current]);

    React.useEffect(() => {
        setEditComment(_editComment);
    }, [_editComment]);

    React.useEffect(() => {
        onDidMount();
        return onClose;
    }, []);

    const isMarkdown = !0 || HTMLEditorType === EHTMLEditorType.MARKDOWN;

    React.useEffect(() => {
        if (editComment || replyComment) {
            setShowReply(true);
            const input = inputRef.current && inputRef.current.getInput();
            if (input) {
                input.focus();
                if (editComment) {
                    setTimeout(() => {// подождать новый текст в инпуте
                        input.select();
                    }, 0);
                }
            }
        }
    }, [editComment, replyComment]);

    React.useEffect(() => {
        if (!editComment) {
            onChangeCommentValue(value);
        }
        setInputValue(value);
        setValueEditor(value);
    }, [value]);

    React.useEffect(() => {
        setAttachments(driveDocs);
    }, [driveDocs]);

    React.useEffect(() => {
        setSendDisabled(
            (!attachments || !attachments.length) &&
            (!inputValue || !inputValue.trim()) &&
            (!valueEditor || !valueEditor.trim())
        );
        setShowAttachments(attachments && attachments.length);
    }, [attachments, value, inputValue, valueEditor]);

    React.useEffect(() => {
        if (!editComment) return;
        const isValueChanged = isMarkdown ? inputValue !== editComment.text : valueEditor !== editComment.text;
        const isAttachmentChanged = !isEqual(attachments, driveDocs);
        setChanged(isValueChanged || isAttachmentChanged);
    }, [editComment, inputValue, attachments, replyComment, valueEditor]);

    const [replyAttachmentThumbUrl, setReplyAttachmentThumbUrl] = React.useState('');
    React.useEffect(() => {
        if (!replyAttachment || !isShowReplyAttachment) return;

        if (msFileThumbnailStorage.is()) {
            msFileThumbnailStorage.getThumbnail(replyAttachment.driveId, replyAttachment.fileId, `c${THUMB_COMMENT_REPLY_SIZE}x${THUMB_COMMENT_REPLY_SIZE}`)
                .then(thumb => setReplyAttachmentThumbUrl(thumb.url));
        } else if (googleFileThumbnailStorage.is()) {
            googleFileThumbnailStorage.getThumbnail(replyAttachment.fileId, COMMENTS_INPUT_GOOGLE_THUMB_SIZE)
                .then(thumb => setReplyAttachmentThumbUrl(thumb));
        }
    }, [replyAttachment]);

    const [extraHeight, setExtraHeight] = React.useState(0);
    const timerRef = React.useRef(null);
    React.useEffect(() => {
        if (isVirtualList) {
            if (!ref.current) {
                setInputHeight(0);
            } else {
                if (timerRef.current) clearTimeout(timerRef.current);
                timerRef.current = setTimeout(() => { // подождать окончания анимации
                    const height = ref.current && ref.current.offsetHeight;
                    setInputHeight(height);

                    if (replyComment || editComment || (attachments && attachments.length) || inputValue) {
                        const newExtraHeight = -extraHeight + height - DEFAULT_INPUT_HEIGHT;
                        if (newExtraHeight) {
                            setExtraHeight(prev => prev + newExtraHeight);
                        }
                        onScrollTop(newExtraHeight);
                    } else {
                        onScrollTop(-extraHeight);
                        setExtraHeight(0);
                    }
                }, COMMENT_REPLY_ANIMATION_DURATION + 200);
            }
        } else {
            if (!inputRef.current || document.activeElement !== inputRef.current.getInput()) return;

            const isNoAsideHeader = !!document.querySelector('.' + CLASS_ASIDE_PANEL + '--without-header');
            const asidePanel = document.querySelector('.' + CLASS_ASIDE_PANEL) as HTMLElement;
            if (!asidePanel) return;
            asidePanel.classList.add(isNoAsideHeader ? CLASS_ASIDE_PANEL_STATIC_NO_HEADER : CLASS_ASIDE_PANEL_STATIC);
            if (timerRef.current) clearTimeout(timerRef.current);
            timerRef.current = setTimeout(() => { // подождать окончания анимации
                const el = ref.current as HTMLElement;
                if (!el) return;
                const scrollEL = document.querySelector('.' + CLASS_ASIDE_PANEL_BODY) as HTMLElement;
                if (!scrollEL) return;
                if (replyComment || editComment || (attachments && attachments.length) || inputValue) {
                    const scrollEL = document.querySelector('.' + CLASS_ASIDE_PANEL_BODY) as HTMLElement;
                    if (!scrollEL) return;
                    const height = -extraHeight + el.offsetHeight - DEFAULT_INPUT_HEIGHT;
                    if (height) setExtraHeight(prev => prev + height);
                    if (scrollEL.scrollTop < scrollEL.scrollHeight - scrollEL.offsetHeight - GOOGLE_SPACING * 1.5) {
                        scrollEL.scrollTo({ top: scrollEL.scrollTop + height, behavior: 'smooth' });
                    }
                } else {
                    if (scrollEL.scrollTop < scrollEL.scrollHeight - scrollEL.offsetHeight - GOOGLE_SPACING * 1.5) {
                        scrollEL.scrollTo({ top: scrollEL.scrollTop - extraHeight, behavior: 'smooth' });
                    }
                    setExtraHeight(null);
                }
                setTimeout(() => {
                    const asidePanel = document.querySelector('.' + CLASS_ASIDE_PANEL) as HTMLElement;
                    const asidePanelBody = document.querySelector('.' + CLASS_ASIDE_PANEL + '--card') as HTMLElement;
                    if (!asidePanel || !asidePanelBody) return;
                    if (isNoAsideHeader) {
                        asidePanelBody.classList.add(CLASS_ASIDE_PANEL + '--without-header');
                    } else {
                        asidePanelBody.classList.remove(CLASS_ASIDE_PANEL + '--without-header');
                    }
                    asidePanel.classList.remove(isNoAsideHeader ? CLASS_ASIDE_PANEL_STATIC_NO_HEADER : CLASS_ASIDE_PANEL_STATIC);
                }, COMMENT_REPLY_ANIMATION_DURATION);
            }, COMMENT_REPLY_ANIMATION_DURATION + 100);
        }
    }, [replyComment, editComment, attachments, inputValue]);

    React.useEffect(() => {
        setInputValue(value);
    }, [value]);

    React.useEffect(() => {
        if (!inputValue) onChangeCommentValue('');
    }, [inputValue]);

    React.useEffect(() => {
        if (!isVirtualList) return;
        const list = document.querySelector(`.${CLASS_COMMENTS}-list__virtual-list`) as HTMLElement;
        if (!list) {
            setFloating(false);
            return;
        }
        const observer = new MutationObserver(() => {
            setFloating(list.scrollHeight > list.clientHeight);
        });
        observer.observe(list, {attributes: false, childList: true, characterData: false, subtree: true});
        return () => observer.disconnect();
    }, []);

    const floatingTimerRef = React.useRef(null);
    React.useEffect(() => {
        if (isVirtualList) return;
        const bodyEl = document.querySelector('.' + CLASS_ASIDE_PANEL_BODY) as HTMLElement;
        if (!bodyEl) return;
        const observer = new MutationObserver(() => {
            if (floatingTimerRef.current) clearTimeout(floatingTimerRef.current);
            floatingTimerRef.current = setTimeout(() => {
                setFloating(commentsCount && bodyEl.scrollHeight > bodyEl.offsetHeight);
            }, 200);
        });
        observer.observe(bodyEl, {attributes: false, childList: true, characterData: false, subtree: true});
        return () => observer.disconnect();
    }, []);

    const droppedAttachmentsRef = React.useRef(null);
    React.useEffect(() => {
        const droppedAttachmentsStr = droppedAttachments.join('');
        if (droppedAttachmentsStr === droppedAttachmentsRef.current) return;
        droppedAttachmentsRef.current = droppedAttachmentsStr;
        if (droppedAttachments && droppedAttachments.length) {
            setRemoveDriveDocIds([...removeDriveDocIds, ...droppedAttachments.map(driveDoc => driveDoc.id)]);
            const newAttachments = [...attachments, ...droppedAttachments];
            setAttachments(newAttachments);
            onAttachmentsChange(newAttachments);
            if (!isVirtualList) fixInput();
            const input = inputRef.current && inputRef.current.getInput();
            if (!input) return;
            input.focus();
        }
    }, [droppedAttachments]);

    const isShowDialog = platformType && platformType === EAuthUserPlatformType.MICROSOFT;

    const attachmentButton = <Button
        className={classAttachment}
        tooltip={{
            isNoEvents: true,
            value: CARD_SECTION_COMMENTS_ADD_ATTACHMENT
        }}
        variant={'icon'}
        onClick={onAttachmentAdd}
    >
        <Icon
            size={24}
            xlink={'attachment'}
        />
    </Button>;

    if (!isShow) return null;

    return (
        <div
            className={`
                ${className}
                ${!isMarkdown ? className + '--editor' : ''}
                ${isShowReply ? className + '--with-reply' : ''}
                ${isShowAttachments ? className + '--with-attachments' : ''}
                ${isFloating ? className + '--floating' : ''}
                handle-click-ignore
            `}
            ref={ref}
        >
            <div className={replyClass + '-wrapper'}>
                {(replyComment || editComment) &&
                    <div
                        className={`
                            ${replyClass}
                            ${editComment ? replyClass + '--edit' : ''}
                            ${replyAttachment ? replyClass + '--attachment' : ''}
                        `}
                        onClick={onCommentClick}
                    >
                        <div className={replyClass + '-content'}>
                            <Icon className={replyClass + '-icon'} xlink={editComment ? 'pen' : 'reply'} size={24} />
                            {replyAttachment &&
                                <div className={replyClass + '-attachment'}>
                                    <img
                                        className={replyClass + '-img'}
                                        onError={()=> {
                                            if (googleFileThumbnailStorage.is()) {
                                                googleFileThumbnailStorage.reGetThumbnail(
                                                    replyAttachment.fileId,
                                                    COMMENTS_INPUT_GOOGLE_THUMB_SIZE,
                                                    setReplyAttachmentThumbUrl,
                                                );
                                            }
                                        }}
                                        src={replyAttachment.isImage ? replyAttachmentThumbUrl : getBigPreviewIcon(replyAttachment.iconUrl)}
                                    />
                                </div>
                            }
                            <div className={replyClass + '-body'}>
                                {editComment && <div className={replyClass + '-edit'}>{COMMENT_EDIT_LABEL}</div>}
                                {replyComment && <div className={replyClass + '-author'}>{replyComment.author.fullName}</div>}
                                <div className={replyClass + '-text'}>
                                    {replyAttachment && !isShowReplyAttachment ? replyAttachment.title :
                                        editComment ?
                                            editComment.text || COMMENT_NOT_TITLE_LABEL
                                            : replyComment && replyComment.text ?
                                                replyText ?
                                                    replyText
                                                    : replyComment.text
                                                : COMMENT_NOT_TITLE_LABEL
                                    }
                                </div>
                            </div>
                        </div>
                        <Button
                            variant={'icon'}
                            onClick={e => {
                                e.stopPropagation();
                                setShowReply(false);
                                const input = inputRef.current && inputRef.current.getInput();
                                if (input) input.focus();
                                setTimeout(() => { // ждем окончания анимации
                                    if (editComment) {
                                        closeHandle();
                                    } else {
                                        onReplyClose();
                                    }
                                }, COMMENT_REPLY_ANIMATION_DURATION);
                            }}
                        >
                            <Icon xlink={'clear'} size={24} />
                        </Button>
                    </div>
                }
            </div>
            <div className={className + '-attachments-wrapper'}>
                {attachments && !!attachments.length &&
                    <CommentAttachments attachments={attachments} onDelete={onDeleteAttachment} />
                }
            </div>
            <div className={className + '-content'}>
                {isMarkdown ?
                    <Input
                        ref={inputRef}
                        autoFocus={autoFocus}
                        className={className + '-text'}
                        label={COMMENT_LABEL}
                        placeholder={COMMENT_PLACEHOLDER}
                        value={inputValue}
                        onEnter={enterHandle}
                        onKeyDown={keyDownHandle}
                        onChange={changeHandler}
                        onBlur={onBlurHandle}
                    />
                    : <HtmlEditorHOC
                        cardId={cardId}
                        placeholder={COMMENT_PLACEHOLDER}
                        selectionTree={null}
                        type={EEditorType.NEW_COMMENT}
                        value={blockMarkdown(valueEditor)}
                        onChange={handleHtmlChange}
                        onKeyDown={keyDownHandle}
                        // todo autoFocus={autoFocus}
                        {...editorOnEnter}
                    />
                }
                <Button
                    className={`${buttonClass} ${editComment ? buttonClass + '--save' : ''}`}
                    variant={'icon'}
                    onClick={createHandle}
                    disabled={editComment && !isChanged || isSendDisabled}
                >
                    {editComment
                        ? <Icon className={buttonClass + '-done'} xlink={'done-circle'} size={16} />
                        : <Icon xlink={'send'} size={24} />
                    }
                </Button>
                <CommentsMentionsHOC
                    boardId={boardId}
                    cardId={cardId}
                    openedProperty={openedProperty}
                />
                {isShowDialog ?
                    <ButtonDropdown
                        className={classAttachment + '-wrapper'}
                        dropdownClassName={classAttachment + '-wrapper-dropdown'}
                        opened={isOpen}
                        onOpen={() => setOpen(true)}
                        onClose={() => setOpen(false)}
                    >
                        {attachmentButton}
                        <CardAttachmentAdd
                            onSelectClick={isShowSelectFile ? () => onSelectClick(() => setOpen(false), afterAttachmentAdd) : null}
                            onUploadClick={(onStart, onProgress, onFinish) => onUploadClick(onStart, onProgress, () => {
                                onFinish();
                                setOpen(false);
                            }, afterAttachmentAdd)}
                        />
                    </ButtonDropdown>
                    : attachmentButton
                }
            </div>
        </div>
    );
}
